import type { AccessTokenProvider } from "~application/auth/ports/access-token-provider.ts";
import type { BackofficeAuthenticationResponse } from "~infrastructure/api/backoffice/contract/authentication/responses/authentication-responses.ts";
import type { TAppConfig } from "~ui/@application/configs/config-schema.ts";

import { type ApiFetcher, type ApiFetcherArgs, initClient, tsRestFetchApi } from "@ts-rest/core";
import { ELocale } from "~application/constants/locale.constant.ts";
import { backofficeContract } from "~infrastructure/api/backoffice/contract/backoffice-contract.ts";
import { sentryLogBackofficeTsRestException } from "~ui/@core/composables/sentry/use-sentry.ts";
import { isStatusCodeSuccess } from "~utils/http-utils.ts";

type ExtraArgs = {};

function buildCommaQuery(rawQuery: Record<string, unknown> | undefined): string {
  if (!rawQuery) return "";

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(rawQuery)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      // foo=abc,def,ghi
      if (value.length === 0) continue;
      params.set(key, value.join(","));
    } else {
      params.set(key, String(value));
    }
  }

  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function withCommaQuery<T extends ApiFetcherArgs>(args: T): T {
  const [base] = args.path.split("?", 1); // on vire la query construite par ts-rest
  const search = buildCommaQuery(args.rawQuery as Record<string, unknown> | undefined);

  return {
    ...args,
    path: `${base}${search}`,
  };
}

function getLocalizedAcceptLanguage() {
  const mapping: Record<string, string> = {
    [ELocale.FRENCH]: "fr",
    [ELocale.ENGLISH]: "en",
    fr: "fr",
    en: "en",
  };

  const locale = document.documentElement.getAttribute("lang");
  return !locale ? "fr" : (mapping[locale] ?? "fr");
}

export function createBackofficeTsRestClient(env: TAppConfig, tokenProvider: AccessTokenProvider) {
  const api: ApiFetcher = async (originalArgs: ApiFetcherArgs & ExtraArgs) => {
    const args = withCommaQuery(originalArgs);

    const maybeToken = await tokenProvider.getAccessToken();
    const token = args.route.path.startsWith("/api") && maybeToken.success ? maybeToken.value : null;
    const serviceId = env.VITE_VOLTEXPLOIT_SERVICE_ID.toString();
    const withAuth: ApiFetcherArgs = {
      ...args,
      headers: {
        ...args.headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        "Accept-Language": getLocalizedAcceptLanguage(),
        "Voltalis-ServiceId": serviceId,
        "X-Voltalis-ServiceId": serviceId, // deprecated, kept for backward compat
        serviceId: serviceId, // deprecated, kept for backward compat
      },
    };

    let res = await tsRestFetchApi(withAuth);

    if (token && res.status === 401) {
      const maybeRefreshToken = await tokenProvider.getRefreshToken();
      if (maybeRefreshToken.success) {
        const resp = await tsRestFetchApi({
          ...args,
          method: "POST",
          path: `${env.VITE_BACKOFFICE_BASE_URL}/login/refresh`,
          body: JSON.stringify({
            refresh_token: maybeRefreshToken.value,
          }),
          headers: {
            ...args.headers,
            "content-type": "application/json",
            "Accept-Language": getLocalizedAcceptLanguage(),
          },
        });

        if (resp.status === 200) {
          const body = resp.body as BackofficeAuthenticationResponse;
          await tokenProvider.saveAccessToken?.(body.access_token);
          await tokenProvider.saveRefreshToken?.(body.refresh_token);
          const token = args.route.path.startsWith("/api") ? body.access_token : null;

          res = await tsRestFetchApi({
            ...args,
            headers: {
              ...args.headers,
              ...(token ? { authorization: `Bearer ${token}` } : {}),
            },
          });
        }
      }
    }

    if (!isStatusCodeSuccess(res.status)) {
      await sentryLogBackofficeTsRestException(res, args);
    }

    return res;
  };

  return initClient(backofficeContract, {
    baseUrl: env.VITE_BACKOFFICE_BASE_URL,
    api,
    validateResponse: env.VITE_BACKOFFICE_VALIDATE_RESPONSE,
  });
}

export type BackofficeApi = ReturnType<typeof createBackofficeTsRestClient>;
